import "rc-dock/dist/rc-dock.css";

import { useTranslation } from "react-i18next";
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	Suspense,
} from "react";
import { Float, Icon } from "uikit";
import moment from "moment";
import "moment/locale/ru";
import "moment/locale/uk";
import "moment/locale/az";
import "moment/locale/tr";
import "moment/locale/ro";
import "moment/locale/ka";
import "moment/locale/pl";
import { LocalizationProvider } from "@mui/x-date-pickers";
import {
	ru as localeRu,
	uk as localeUk,
	tr as localeTr,
	ro as localeRo,
	enUS as localeEn,
	pl as localePl,
} from "date-fns/locale";
import {
	ruRU,
	enUS,
	ukUA,
	trTR,
	roRO,
	plPL,
} from "@mui/x-date-pickers/locales";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import { SIPToDispatcher } from "./services";
import "./assets/icons";
import {
	Language,
	languagePackages,
	ConstantLanguage,
	ValueLanguage,
} from "./assets/languages/langs";
import { getApplicationConfig, getCurrentIP } from "./utils";
import { callUp, getAndCallDown, sipConnect, sipDisconnect } from "./utils/jsSip";
import {
	getStoredClaimSipId,
	releaseSipRegistration,
} from "./utils/sipRegisterHold";
import sipFlowLog from "./utils/sipFlowLog";
import createRPCQuery from "./utils/createRPCQuery.util";
import { RouterSwitch } from "./routes";
import { useTypedDispatch, useTypedSelector } from "./redux/store";
import Session from "./redux/services/Session";
import serverSettings from "./redux/reducers/applicationConfig";
import currentIP from "./redux/reducers/currentIP";
import sipToDispatcher from "./redux/reducers/sipToDispatcher";
import softphoneWidget from "./redux/reducers/softphoneWidget";
import {
	useKeyBindInit,
	useSettingsInit,
	useGlobalSettings,
	useOrderSettings,
	usePersistRoot,
	useTaxiServiceFilterAccessControl,
	useTaxiServiceDefault,
} from "./hooks";
import { getPRPC } from "./hooks/usePRPC";
import useConnectionQualityMonitor from "./hooks/useConnectionQualityMonitor";
import { SuspenseLoader } from "./components/common";
import { KeyBindLayer, CallProvider, KeyBind } from "./components";
import { DevToolbar, PerfProfiler } from "./tests";

function getMUILocale(language: ValueLanguage) {
	const localeDefault = enUS;

	switch (language) {
		case ConstantLanguage.RU:
			return ruRU;
		case ConstantLanguage.UK:
			return ukUA;
		case ConstantLanguage.TR:
			return trTR;
		case ConstantLanguage.RO:
			return roRO;
		case ConstantLanguage.EN:
			return enUS;
		case ConstantLanguage.AZ: // * no support
			return localeDefault;
		case ConstantLanguage.KA: // * no support
			return localeDefault;
		case ConstantLanguage.PL:
			return plPL;
		default:
			return localeDefault;
	}
}

const SIP_RECLAIM_RETRY_MS = 1_000;
const SIP_RECLAIM_RETRY_MAX_MS = 15_000;
const SIP_RECLAIM_WAIT_MS = 200;

const fetchTranslations = async (lang) => {
	const prpcow = getPRPC();

	if (!prpcow?.theirsModel?.language?.getLanguage) return null;

	const res = await createRPCQuery(
		() => prpcow?.theirsModel.language.getLanguage({ lang }),
		{ silent: false, error: true },
	);

	return res;
};

const App: React.FC = () => {
	const prpcow = getPRPC();
	const { accessCompanies } = useTaxiServiceFilterAccessControl<any>();
	const { company: companyNameDefault } = useTaxiServiceDefault();

	const dispatch = useTypedDispatch();
	const { i18n } = useTranslation();
	const token = useTypedSelector((state) => state.session.token);
	const language = useTypedSelector((state) => state.session.language);
	const languageUser = useTypedSelector(
		(state) => state.account.user?.settings?.language,
	);

	const dateFnsLocales: Record<Language, Locale> = useMemo(() => {
		const localeDefault = localeEn;
		return {
			ru: localeRu,
			uk: localeUk,
			tr: localeTr,
			ro: localeRo,
			en: localeEn,
			az: localeDefault, // * no support
			ka: localeDefault, // * no support
			pl: localePl,
		};
	}, []);

	const adapterLocale = useMemo(
		() => dateFnsLocales[language] || enUS,
		[dateFnsLocales, language],
	);

	const connected = useTypedSelector((state) => state.prpc.connected);
	const authorized = useTypedSelector((state) => state.prpc.authorized);
	const id = useTypedSelector((state) => state.account.user?.id);
	const dispatcherId = useTypedSelector(
		(state) => state.sipToDispatcher.id,
	);
	const uaConfig = useTypedSelector((state) => state.sipToDispatcher.uaConfig);
	const sipValue = useTypedSelector((state) => state.sipToDispatcher.sipValue);

	const prpcowGetLanguageHash = useMemo(
		() => !!prpcow?.theirsModel?.language?.getLanguage,
		[prpcow?.theirsModel?.language?.getLanguage],
	);

	const updateTranslate = useCallback(
		(data, languageUser) => {
			if (!data || data?.error) return;

			// Merge local `dev.yml` under server translations so newly added
			// keys (e.g. archive-edit payment notes) still resolve even when
			// the browser already had an older language payload cached, or
			// the active lang file on the server is briefly behind.
			const localFallback =
				languagePackages[languageUser as Language]?.translation ||
				languagePackages.uk.translation;
			const merged = {
				...localFallback,
				...data,
				transactionDescription: {
					...(localFallback as any)?.transactionDescription,
					...(data as any)?.transactionDescription,
					archiveEditDeltaReasons: {
						...(localFallback as any)?.transactionDescription
							?.archiveEditDeltaReasons,
						...(data as any)?.transactionDescription
							?.archiveEditDeltaReasons,
					},
					orderTypes: {
						...(localFallback as any)?.transactionDescription
							?.orderTypes,
						...(data as any)?.transactionDescription?.orderTypes,
					},
				},
			};

			i18n.addResources(languageUser, "translation", merged).init({
				lng: languageUser,
				fallbackLng: "dev",
				debug: false,
				resources: {
					[languageUser]: {
						translation: merged,
					},
					dev: {
						translation: languagePackages.uk.translation,
					},
				},
				preload: false,
				react: {
					useSuspense: false,
				},
				initImmediate: false,
				interpolation: {
					escapeValue: false,
				},
			});

			i18n.changeLanguage(languageUser);
		},
		[i18n],
	);

	// Initialize with default language
	useEffect(() => {
		if (prpcowGetLanguageHash) {
			const activeLanguage = languageUser || language;

			fetchTranslations(activeLanguage).then((data) => {
				updateTranslate(data, activeLanguage);
			});
		}
	}, [languageUser, language, prpcowGetLanguageHash, updateTranslate]);

	useEffect(() => {
		dispatch(Session.create(token?.value));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token]);

	useEffect(() => {
		(async () => {
			if (connected) {
				dispatch(currentIP.actions.setValue(await getCurrentIP()));
			}
		})();
	}, [dispatch, connected]);

	useEffect(() => {
		(async () => {
			if (!connected) return;

			const applicationConfig = await getApplicationConfig();

			if (!applicationConfig) return;

			dispatch(serverSettings.actions.setConfig(applicationConfig));
		})();
	}, [dispatch, connected]);

	usePersistRoot(prpcow);
	useConnectionQualityMonitor();
	useGlobalSettings();
	useOrderSettings();
	useSettingsInit();
	useKeyBindInit();

	useEffect(() => {
		moment.locale(language);
	}, [language]);

	const localeText = useMemo(
		() =>
			getMUILocale(language).components.MuiLocalizationProvider
				.defaultProps.localeText,
		[language],
	);

	const initSipSession = useCallback(async () => {
		const sipData = sessionStorage.getItem("sipData");
		if (!sipData) return;

		const parsedSipData = JSON.parse(sipData);
		const { lineType, sipToDispatcherId, linkId, sipValue } = parsedSipData;

		sipFlowLog("App restore: from sessionStorage", {
			claimSipId: sipToDispatcherId,
			linkId,
			sipValue,
			lineType,
		});

		// Claim/session is keyed by the SIP id (`sipToDispatcherId`).
		// allowSameUserReclaim: after F5 PRPC sessionId changes but Redis may
		// still hold this tab's previous lock — reclaim own line only.
		let data = await SIPToDispatcher.initSession({
			sipToDispatcherId,
			allowSameUserReclaim: true,
		});

		// After refresh, pagehide may clear Redis slightly after this claim.
		if (!data?.success) {
			await new Promise((resolve) => {
				setTimeout(resolve, 400);
			});
			data = await SIPToDispatcher.initSession({
				sipToDispatcherId,
				allowSameUserReclaim: true,
			});
		}

		sipFlowLog("App restore: claim result", {
			claimSipId: sipToDispatcherId,
			success: data?.success === true,
		});

		if (data?.success) {
			dispatch(
				sipToDispatcher.actions.setSip({
					// State id must be the link id (getById / calls resolve by it);
					// fall back to sipToDispatcherId for pre-existing storage.
					id: linkId ?? sipToDispatcherId,
					sipValue,
				}),
			);
			dispatch(sipToDispatcher.actions.setLineType(lineType));
		}
	}, [dispatch]);

	// Bumped on every reclaim start and on disconnect / logout, so an older
	// reclaim loop sees it is stale and stops.
	const sipReclaimGenerationRef = useRef(0);

	const reclaimSipAfterReconnect = useCallback(async () => {
		const claimSipId = getStoredClaimSipId();
		if (claimSipId == null) return;

		sipReclaimGenerationRef.current += 1;
		const generation = sipReclaimGenerationRef.current;
		const { selectionVersion } = SIPToDispatcher;
		// Stale: a newer reconnect started, or the dispatcher picked / dropped
		// a line meanwhile. Then no new request may be sent and no answer may
		// touch the selection.
		const isStale = () =>
			generation !== sipReclaimGenerationRef.current ||
			selectionVersion !== SIPToDispatcher.selectionVersion ||
			getStoredClaimSipId() !== claimSipId;

		// Retry until it is answered or stale: while the lock points at the dead
		// session, calls open order cards nobody sees.
		for (let attempt = 1; ; attempt += 1) {
			// Wait for the dispatcher's own pick / release: sending now would
			// give the reclaim a higher claimSeq and override that pick.
			while (SIPToDispatcher.selectionInFlight) {
				await new Promise((resolve) => {
					setTimeout(resolve, SIP_RECLAIM_WAIT_MS);
				});
				if (isStale()) return;
			}
			if (isStale()) return;

			const data = await SIPToDispatcher.initSession(
				{
					sipToDispatcherId: claimSipId,
					allowSameUserReclaim: true,
				},
				{ reclaim: true },
			);
			if (isStale()) return;

			sipFlowLog("App reconnect: reclaim result", {
				claimSipId,
				attempt,
				success: data?.success,
				reason: data?.reason,
			});

			// Redux kept the selection through the reconnect, so there is
			// nothing to set; re-setting it could re-register the softphone.
			// If the softphone's own WSS dropped too, its registration waits
			// for exactly this confirmation (sipRegisterHold.ts).
			if (data?.success === true) {
				releaseSipRegistration();
				return;
			}

			// The server already applied a newer request of this tab.
			if (data?.reason === "stale") return;

			// Explicit refusal: another session holds the line or it is gone
			// (an older server sends no reason).
			if (
				data?.success === false &&
				(!data.reason ||
					data.reason === "busy" ||
					data.reason === "unknown_sip")
			) {
				await sipDisconnect({ skipSessionClear: true });
				return;
			}

			// No answer (RPC error / timeout) or a transient refusal
			// (lock_timeout): keep the selection and retry.
			await new Promise((resolve) => {
				setTimeout(
					resolve,
					Math.min(
						SIP_RECLAIM_RETRY_MS * 2 ** (attempt - 1),
						SIP_RECLAIM_RETRY_MAX_MS,
					),
				);
			});
		}
	}, []);

	const clearStoredSipSelection = useCallback(() => {
		sessionStorage.removeItem("sipToDispatcherId");
		sessionStorage.removeItem("dispatcherId");
		sessionStorage.removeItem("sipData");
	}, []);

	// User id the initial (page-load) SIP restore already ran for. Any later
	// `connected` false -> true for the same user is a PRPC reconnect.
	const sipRestoreUserIdRef = useRef<number | null>(null);

	useEffect(() => {
		// Logout: the next login is a fresh start, not a reconnect.
		if (!id) sipRestoreUserIdRef.current = null;
		if (!id || !connected || !authorized) {
			// Stop a running reclaim loop; the next reconnect starts a new one.
			sipReclaimGenerationRef.current += 1;
			return;
		}

		if (sipRestoreUserIdRef.current === id) {
			// PRPC reconnect: the server gave us a new sessionId, but the Redis
			// lock of our line still points at the dead one, so incoming calls
			// would open the order card in a session nobody listens to.
			// Reclaim the line for the new session instead of dropping it.
			reclaimSipAfterReconnect();
			return;
		}

		sipRestoreUserIdRef.current = id;

		const navType = performance.getEntriesByType(
			"navigation",
		)[0] as PerformanceNavigationTiming | undefined;
		const isReload = navType?.type === "reload";

		// F5 / reload: restore previously selected line.
		// Tab close → open (or reopen closed tab): do NOT auto-connect — user must click.
		if (isReload && sessionStorage.getItem("sipData")) {
			initSipSession();
			return;
		}

		clearStoredSipSelection();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [connected, authorized, id]);

	useEffect(() => {
		let reloadIntent = false;

		const onKeyDown = (event: KeyboardEvent) => {
			if (
				event.key === "F5" ||
				((event.ctrlKey || event.metaKey) &&
					event.key.toLowerCase() === "r")
			) {
				reloadIntent = true;
			}
		};

		const onPageHide = (event: PageTransitionEvent) => {
			// bfcache restore — do not tear down
			if (event.persisted) return;

			if (reloadIntent) {
				// Reload: release THIS tab's Redis lock (new PRPC sessionId
				// cannot keep the old lock), but keep sipData for restore.
				sipDisconnect({
					skipLocalClear: true,
					onlyCurrentSession: true,
				}).catch(() => undefined);
				return;
			}

			// Tab/browser close: unbind only THIS tab's Redis session.
			// onlyCurrentSession avoids a late pagehide wiping a new claim after reopen.
			sipDisconnect({
				skipLocalClear: true,
				onlyCurrentSession: true,
			}).catch(() => undefined);
		};

		window.addEventListener("keydown", onKeyDown);
		window.addEventListener("pagehide", onPageHide);
		return () => {
			window.removeEventListener("keydown", onKeyDown);
			window.removeEventListener("pagehide", onPageHide);
		};
	}, []);

	const clearAndUpdateSipToDispatcher = useCallback(async () => {
		if (dispatcherId) {
			if (uaConfig) {
				await sipDisconnect({
					skipSessionClear: true,
					skipLocalClear: true,
				});
				dispatch(softphoneWidget.actions.clearCalls());
				dispatch(softphoneWidget.actions.setSelectedCaller(null));
			}

			sipFlowLog("App: getById request", {
				stateId: dispatcherId,
				expectedSipValue: sipValue,
			});
			const data = await SIPToDispatcher.getById(dispatcherId);
			sipFlowLog("App: getById resolved", {
				stateId: dispatcherId,
				resolvedSipValue: data?.sip?.sipValue,
				resolvedSipId: data?.sip?.id,
			});

			// Guard: getById resolves by the sipToDispatcher link id. If the id
			// ever points at another line (id mismatch / stale cache), never
			// register a line the user didn't pick — abort instead of dialing
			// e.g. an external peer.
			if (
				sipValue &&
				data?.sip?.sipValue &&
				data.sip.sipValue !== sipValue
			) {
				console.error(
					"[SIP] getById resolved a different line — aborting connect",
					{
						expected: sipValue,
						got: data.sip.sipValue,
						id: dispatcherId,
					},
				);
				return;
			}

			sipFlowLog("App: register ->", {
				sipValue: data?.sip?.sipValue,
				resolvedSipId: data?.sip?.id,
			});
			await sipConnect(data?.sip);
		}
	}, [dispatch, dispatcherId, uaConfig, sipValue]);

	const initDispatcherSip = useCallback(async () => {
		clearAndUpdateSipToDispatcher();
	}, [clearAndUpdateSipToDispatcher]);

	useEffect(() => {
		initDispatcherSip();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatcherId]);

	useEffect(() => {
		const defaultTitle = "ES Cabinet";
		const companyNameFirst = accessCompanies?.[0]?.name?.[language];
		document.title = companyNameDefault || companyNameFirst || defaultTitle;
	}, [accessCompanies, companyNameDefault, language]);

	return (
		<KeyBindLayer>
			<LocalizationProvider
				dateAdapter={AdapterDateFns}
				adapterLocale={adapterLocale}
				localeText={localeText}
			>
				<Icon.Provider />
				{connected && sipValue && <CallProvider />}

				<Float.Container id="root">
					<Float.Tracker id="root">
						<Suspense fallback={<SuspenseLoader />}>
							<KeyBind
								settingId="callUpSoftphone"
								onUse={() => callUp(true)}
							/>
							<KeyBind
								settingId="callDownSoftphone"
								onUse={() => getAndCallDown()}
							/>
							<DevToolbar />
							<PerfProfiler id="App">
								<RouterSwitch />
							</PerfProfiler>
						</Suspense>
					</Float.Tracker>
				</Float.Container>
			</LocalizationProvider>
		</KeyBindLayer>
	);
};

export default App;
